import { useState } from 'react';
import { Text, TouchableOpacity, View } from 'react-native';
import { AppStoreButtons } from '../components/AppStoreButtons';
import { Avatar, AuthField, OutlinePill, PillRow, PremiumPill, QuickAction } from '../components/ui';
import { AuthChrome } from '../components/AuthChrome';
import { FeaturedEventCard } from '../components/FeaturedEventCard';
import { PwaInstallInstructions } from '../components/PwaInstallInstructions';
import { getLegalDocs } from '../components/legal';
import { seed } from '../data/seed';
import { formatHandicapIndex, resolveHandicapIndex } from '../handicap';
import { styles } from '../theme';
import type { LegalDocKey, Screen, Translation } from '../types';

export { CreateAccountScreen } from './CreateAccountScreen';

export function LandingScreen({
  t,
  setScreen,
}: {
  t: Translation;
  setScreen: (screen: Screen) => void;
}) {
  const player = seed.player;
  const featuredEvent = seed.events?.[0];
  const banner = seed.banners[0];
  const homePills = [t.pillClubs, t.pillCommunities, t.pillRank, t.pillAcademy, t.pillTournaments];
  const openRegister = () => setScreen('createAccount');

  return (
    <AuthChrome t={t} setScreen={setScreen}>
      <PillRow items={homePills} active="" setActive={openRegister} />
      <Text style={styles.sectionTitle}>{t.card}</Text>
      <View style={styles.profileHeroCard}>
        <View style={styles.profileHeroTop}>
          <Avatar large initials={player.initials} />
          <View style={styles.profileHeroCopy}>
            <Text style={styles.profileHeroName}>{player.name}</Text>
            <Text style={styles.profileHeroClub}>{player.club}</Text>
          </View>
        </View>
        <View style={styles.profileHeroPills}>
          <OutlinePill label={`${t.rankPrefix}${player.rank}`} />
          <OutlinePill label={`${t.hcpShort} ${formatHandicapIndex(resolveHandicapIndex(player))}`} />
          <PremiumPill label={t.premium} />
        </View>
      </View>

      {featuredEvent ? (
        <FeaturedEventCard
          t={t}
          title={featuredEvent.title}
          subtitle={featuredEvent.description}
          cta={t.createAccount}
          videoUrl={featuredEvent.videoUrl}
          imageUrl={featuredEvent.imageUrl}
          onPress={openRegister}
        />
      ) : banner ? (
        <FeaturedEventCard
          t={t}
          title={banner.title}
          subtitle={banner.subtitle}
          cta={banner.cta}
          imageUrl={banner.imageUrl}
          onPress={openRegister}
        />
      ) : null}

      <Text style={styles.sectionTitle}>{t.quickAccess}</Text>
      <View style={styles.grid}>
        <QuickAction label={t.findMatch} detail={t.detailFriendsMatch} icon="users" onPress={openRegister} />
        <QuickAction label={t.myRating} detail={t.detailViewStats} icon="trending-up" onPress={openRegister} />
        <QuickAction label={t.academy} detail={t.detailBookLesson} icon="graduation-cap" onPress={openRegister} />
        <QuickAction label={t.communities} detail={t.detailJoinSelect} icon="building" onPress={openRegister} />
      </View>

      <View style={styles.landingActions}>
        <TouchableOpacity style={styles.primaryButton} onPress={openRegister}>
          <Text style={styles.primaryButtonText}>{t.createAccount}</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.secondaryButton} onPress={() => setScreen('welcome')}>
          <Text style={styles.secondaryButtonText}>{t.signIn}</Text>
        </TouchableOpacity>
      </View>

      <AppStoreButtons buttonSoonLabel={t.storeButtonSoon} downloadLabel={t.storeDownloadLabel} />
      <PwaInstallInstructions t={t} />
    </AuthChrome>
  );
}

export function WelcomeScreen({
  t,
  setScreen,
  onSignIn,
}: {
  t: Translation;
  setScreen: (screen: Screen) => void;
  onSignIn: (email: string, password: string) => void | Promise<string | void>;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const emailValid = email.trim().length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  async function handleSignIn() {
    if (submitting) {
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const message = await onSignIn(email.trim(), password);
      if (message) {
        setError(message);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthChrome t={t} setScreen={setScreen}>
      <Text style={styles.subtitle}>{t.welcomeSubtitleWeb}</Text>
      <View style={styles.authForm}>
        <AuthField
          label={t.emailFieldLabel.toUpperCase()}
          value={email}
          onChangeText={setEmail}
          valid={emailValid}
          keyboardType="email-address"
          autoCapitalize="none"
          showLabel={t.showPassword}
          hideLabel={t.hidePassword}
        />
        <AuthField
          label={t.passwordFieldLabel.toUpperCase()}
          value={password}
          onChangeText={setPassword}
          secureTextEntry
          valid={password.length >= 8}
          showLabel={t.showPassword}
          hideLabel={t.hidePassword}
          onSubmitEditing={() => void handleSignIn()}
        />
      </View>
      {error ? <Text style={styles.formErrorText}>{error}</Text> : null}
      <TouchableOpacity disabled={submitting} style={styles.primaryButton} onPress={() => void handleSignIn()}>
        <Text style={styles.primaryButtonText}>{submitting ? t.submittingLabel : t.signIn}</Text>
      </TouchableOpacity>
      <TouchableOpacity style={styles.secondaryButton} onPress={() => setScreen('createAccount')}>
        <Text style={styles.secondaryButtonText}>{t.createAccount}</Text>
      </TouchableOpacity>
    </AuthChrome>
  );
}

export function LegalPageScreen({ t, docKey, onBack }: { t: Translation; docKey: LegalDocKey; onBack: () => void }) {
  const doc = getLegalDocs(t).find(item => item.key === docKey);
  if (!doc) {
    return null;
  }

  return (
    <View style={styles.legalPage}>
      <TouchableOpacity onPress={onBack} style={styles.landingBackLink}>
        <Text style={styles.landingBackLinkText}>← {t.back}</Text>
      </TouchableOpacity>
      <Text style={styles.legalPageTitle}>{doc.title}</Text>
      <Text style={styles.legalPageBody}>{doc.body}</Text>
    </View>
  );
}
